import styles from './PunchupSeatsSection.module.css';
import { FC, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle } from 'lucide-react';

import { EventData } from 'features/common/types';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import {
	PunchupFilterSelect,
	PunchupFilterOption,
} from 'features/common/components/Punchup/primitives/forms/pickers/PunchupFilterSelect';
import { PunchupSeatsWidget } from 'features/common/components/Punchup/composites/seats/PunchupSeatsWidget';
import { PunchupDangerZone } from 'features/common/components/Punchup/composites/forms/PunchupDangerZone';
import { PunchupConfirmDialog } from 'features/common/components/Punchup/composites/dialogs/PunchupConfirmDialog';
import { useOptionalAccordionSection } from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import { useGetSeatsIOEvent } from 'hooks/useGetSeatsIOEvent';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';
import useGetPartnerData from 'features/common/hooks/useGetPartnerData';
import { useResponsiveScreen } from 'hooks/useWindowSize';

// The two danger modes map directly to distinct seats.io operating modes:
//   'manageForSaleConfig'  → toggle individual seats ON / OFF sale (the for-sale flag).
//   'manageObjectStatuses' → override booking status (book / release a seat).
// Both are real seats.io EventManagerModes, so no local aliasing is needed —
// each value is passed straight through to PunchupSeatsWidget.
type SeatsMode = 'static' | 'manageChannels' | 'manageTableBooking' | 'manageForSaleConfig' | 'manageObjectStatuses';

const SEAT_MODES_BASE: Array<PunchupFilterOption & { value: SeatsMode }> = [
	{ value: 'static', label: 'View Chart' },
	{ value: 'manageChannels', label: 'Manage Channels' },
	{ value: 'manageTableBooking', label: 'Manage Table Booking' },
];

// Shown only when the Danger Zone is active — appear with red background to
// signal that these modes directly affect what buyers can see and book.
// 'On sale / Off sale' drives the for-sale config; 'Booked' drives booking status.
const SEAT_MODES_DANGER: Array<PunchupFilterOption & { value: SeatsMode }> = [
	{ value: 'manageForSaleConfig', label: 'On sale / Off sale', danger: true },
	{ value: 'manageObjectStatuses', label: 'Booked', danger: true },
];

interface PunchupSeatsSectionProps {
	event: EventData;
}

const PunchupSeatsSection: FC<PunchupSeatsSectionProps> = ({ event }) => {
	const { t } = useTranslation();
	const { showNotification, TYPES } = useToastNotification();
	const { partnerData } = useGetPartnerData();
	const isLargeScreen = useResponsiveScreen();
	const section = useOptionalAccordionSection();
	// Default mode is `static` (read-only view). The other modes are explicit edit affordances.
	const [mode, setMode] = useState<SeatsMode>('static');
	const [showManualConfirm, setShowManualConfirm] = useState(false);

	const isManualModeActive = mode === 'manageForSaleConfig' || mode === 'manageObjectStatuses';

	// Mode options expand when the Danger Zone is active to surface the two dangerous modes.
	const seatModeOptions = isManualModeActive ? [...SEAT_MODES_BASE, ...SEAT_MODES_DANGER] : SEAT_MODES_BASE;

	// On manual-mode activation the mode selector (top of the section) becomes the active
	// control. Scroll the section header into view so the selector — and the seat the user
	// then clicks — is immediately visible rather than off-screen below the Danger Zone.
	const activateManualMode = () => {
		setMode('manageForSaleConfig');
		setShowManualConfirm(false);
		section?.scrollToSection('start');
	};

	// Channels endpoint — drives the per-channel copy-link list. Shared with the rest of
	// the dashboard via React Query's deduped cache.
	const { data: seatsEventData } = useGetSeatsIOEvent(event.seatsEventKey);

	const channels = seatsEventData?.channels ?? [];
	const widgetUrl = process.env.REACT_APP_WIDGET;

	if (!event.seatsEventKey || !partnerData?.seats_io_secret_key) {
		return null;
	}

	const buildChannelLink = (channelKey: string) => `${widgetUrl}/event/${event.id}/tickets?compCode=${channelKey}`;

	const handleCopy = (channelKey: string) => {
		const link = buildChannelLink(channelKey);
		const doCopy = () => window.parent.postMessage({ action: 'copy-to-clipboard', text: link }, '*');
		if (navigator.clipboard) {
			// Show the iframe toast only on success. The fallback (doCopy) triggers a
			// parent-frame toast via the copy-to-clipboard postMessage handler — showing
			// both would double-notify the user.
			navigator.clipboard
				.writeText(link)
				.then(() => showNotification({ message: t('seats-link-copied'), type: TYPES.SUCCESS }))
				.catch(doCopy);
		} else {
			doCopy();
		}
	};

	return (
		<>
			<div className={styles.root}>
				<div className={styles.chartHeader}>
					{isLargeScreen && (
						<PunchupFilterSelect
							ariaLabel={t('seats-mode-label', 'Seats management mode')}
							className={styles.modeSelector}
							options={seatModeOptions}
							value={mode}
							onChange={(value) => setMode(value as SeatsMode)}
						/>
					)}
				</div>
				<PunchupSeatsWidget eventKey={event.seatsEventKey} mode={mode} />

				{channels.length > 0 && (
					<div className={styles.channelLinks}>
						<h4 className={styles.sectionTitle}>{t('seats-channel-links-title')}</h4>
						{channels.map((channel) => (
							<div key={channel.key} className={styles.channelRow}>
								<div className={styles.channelInfo}>
									<span className={styles.channelName}>{channel.name}</span>
									<span className={styles.channelKey}>{channel.key}</span>
								</div>
								<span className={styles.channelUrl}>{buildChannelLink(channel.key)}</span>
								<PunchupButton
									variant="outline"
									type="button"
									onClick={() => handleCopy(channel.key)}
									text={t('seats-copy-link')}
								/>
							</div>
						))}
					</div>
				)}
				{channels.length === 0 && seatsEventData && <p className={styles.noChannels}>{t('seats-no-channels')}</p>}

				<div className={styles.eventKey}>
					<span className={styles.label}>{t('seats-event-key', 'Seating chart key')}</span>
					<span className={styles.value}>{event.seatsEventKey}</span>
				</div>

				{/* Manual seat management — DangerZone gate. Not in the mode selector dropdown
			    because the confirm step would be skipped on incidental dropdown changes. */}
				<PunchupDangerZone
					title={t('seats-manual-mode-title', 'Danger Zone')}
					description={t(
						'seats-manual-mode-description',
						'Manually override individual seat statuses. This directly affects what seats buyers can see and book, and can cause overbooking.'
					)}
					actions={
						isManualModeActive ? (
							<PunchupButton
								variant="secondary"
								type="button"
								onClick={() => setMode('static')}
								text={t('seats-exit-manual-mode', 'Exit manual mode')}
							/>
						) : (
							<PunchupButton
								variant="destructive"
								type="button"
								icon={<AlertTriangle size={16} />}
								onClick={() => setShowManualConfirm(true)}
								text={t('seats-activate-manual-mode', 'Activate manual mode')}
							/>
						)
					}
				/>
			</div>

			{showManualConfirm &&
				ReactDOM.createPortal(
					<PunchupConfirmDialog
						icon={<AlertTriangle size={24} />}
						iconVariant="destructive"
						title={t('seats-manual-confirm-title', 'Activate manual seat management?')}
						text={t(
							'seats-manual-confirm-body',
							'This lets you override individual seat statuses directly. Changes are visible to buyers immediately and can cause overbooking. Only proceed if you know what you are doing.'
						)}
						buttons={[
							{ label: t('button-cancel'), variant: 'secondary', onClick: () => setShowManualConfirm(false) },
							{
								label: t('seats-activate', 'Activate'),
								variant: 'destructive',
								icon: <AlertTriangle size={16} />,
								onClick: activateManualMode,
							},
						]}
					/>,
					document.body
				)}
		</>
	);
};

export { PunchupSeatsSection };
